/**
 * `scaleUnits.ts` — port of the `KnownUnits` half of
 * `Models/Utils/ScaleUtil.cs`.
 *
 * Campfire stores every length in METRES and converts only for display, so a
 * unit is really just a divisor: `displayed = metres / unitsToScale(unit)`.
 * The measurement layer and the bounding-box dimension pills both read through
 * here, which is what lets one Units dropdown drive both.
 */

/** Verbatim `ScaleUtil.KnownUnits` — order matters, it is a serialized enum. */
export enum KnownUnits {
  Meters = 0,
  Centimeters = 1,
  Millimeters = 2,
  Micrometers = 3,
  Nanometers = 4,
  Feet = 5,
  Inches = 6,
  Unknown = 7,
}

/** Metres per unit — verbatim `ScaleUtil.UnitsToScale`. */
export function unitsToScale(unit: KnownUnits): number {
  switch (unit) {
    case KnownUnits.Meters:
      return 1;
    case KnownUnits.Centimeters:
      return 0.01;
    case KnownUnits.Millimeters:
      return 0.001;
    case KnownUnits.Micrometers:
      return 0.000001;
    case KnownUnits.Nanometers:
      return 0.000000001;
    case KnownUnits.Feet:
      return 0.3048;
    case KnownUnits.Inches:
      return 0.0254;
    default:
      // Unity throws `NotImplementedException` for Unknown. A throw here would
      // take down a render tick, so fall back to metres (a 1× divisor, i.e.
      // "show the stored value") and let the caller keep drawing.
      return 1;
  }
}

/** Verbatim `ScaleUtil.UnitsToShortString` (note the micro sign is U+00B5). */
export function unitsToShortString(unit: KnownUnits): string {
  switch (unit) {
    case KnownUnits.Meters:
      return "m";
    case KnownUnits.Centimeters:
      return "cm";
    case KnownUnits.Millimeters:
      return "mm";
    case KnownUnits.Micrometers:
      return "µm";
    case KnownUnits.Nanometers:
      return "nm";
    case KnownUnits.Feet:
      return "ft";
    case KnownUnits.Inches:
      return "in";
    default:
      return "";
  }
}

/**
 * Dropdown order, verbatim `ScaleUtil.GetOrderedUnits()` — imperial first,
 * then metric descending. NOT the enum's declaration order.
 */
export const ORDERED_UNITS: ReadonlyArray<KnownUnits> = [
  KnownUnits.Feet,
  KnownUnits.Inches,
  KnownUnits.Meters,
  KnownUnits.Centimeters,
  KnownUnits.Millimeters,
  KnownUnits.Micrometers,
  KnownUnits.Nanometers,
];

/**
 * Format a metre distance the way Unity's
 * `MeasurementsLayerModel.GetDistanceWithUnits` does: `$"{d:F2} {short}"` —
 * always two decimals, a space before the unit.
 */
export function formatDistance(metres: number, unit: KnownUnits): string {
  const value = metres / unitsToScale(unit);
  return `${value.toFixed(2)} ${unitsToShortString(unit)}`;
}
